import { useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { AiReceptionistOrg } from '@wetags/ai-receptionist';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useAppFlagStore } from '@frontend/shared';
import { Heading, Text, RadioField, useTooltip, useForm, InlineNotification } from '@frontend/design-system';
import { SCHEDULING_MODES, SchedulingMode } from './types';

type FormConfig = {
  schedulingMode: {
    type: 'radio';
    value?: SchedulingMode;
  };
};

type Props = {
  locationIds: string[];
  mode: SchedulingMode;
  onChange: (newMode: SchedulingMode) => void;
};

export const SchedulingInstructionAccordionForm = ({ locationIds, mode, onChange }: Props) => {
  const { t } = useTranslation('ai-receptionist-settings');
  const isInternalUpdate = useRef(false);

  // TODO: Milind - Remove the wetag once WEAVE_AIR_INTEGRATION CF backfill is complete
  const [isDirectBookingWetagEnabled, isLoadingDirectBookingWetag] = AiReceptionistOrg.useWetag(
    'air-scheduling-settings-enable-direct-booking'
  );
  const { getCustomizationFlagValue } = useAppFlagStore();

  const isWeaveAirIntegrationCFEnabled = getCustomizationFlagValue({
    locationIds,
    featureEnum: Feature.WEAVE_AIR_INTEGRATION,
  });

  const showDirectBookingOption =
    (!isLoadingDirectBookingWetag && isDirectBookingWetagEnabled) || isWeaveAirIntegrationCFEnabled;

  const { getFieldProps, values, seedValues } = useForm<FormConfig, keyof FormConfig>({
    fields: {
      schedulingMode: {
        type: 'radio',
        value: mode,
      },
    },
  });

  const modeRadioFieldProps = getFieldProps('schedulingMode');

  const { triggerProps, tooltipProps, Tooltip } = useTooltip({
    placement: 'top',
  });

  // Sync state from mode prop when it changes externally
  useEffect(() => {
    if (!isInternalUpdate.current) {
      seedValues({
        schedulingMode: mode,
      });
    }
    isInternalUpdate.current = false;
  }, [mode, seedValues]);

  // Handle mode changes reactively
  useEffect(() => {
    if (values.schedulingMode && values.schedulingMode !== mode) {
      isInternalUpdate.current = true;
      onChange(values.schedulingMode as SchedulingMode);
    }
  }, [values.schedulingMode, mode, onChange]);

  return (
    <div css={styles.container}>
      <div css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div css={styles.header}>
          <Heading level={3}>{t('Scheduling')}</Heading>
          <Text color='subdued' size='medium'>
            {t('Instruct AI Receptionist how to help you with scheduling.')}
          </Text>
        </div>
      </div>

      <RadioField {...modeRadioFieldProps} css={styles.radioFieldContainer}>
        <RadioField.Radio css={styles.radio} value={SCHEDULING_MODES.DO_NOT_HELP}>
          {t('Do not help with scheduling')}
        </RadioField.Radio>

        <div
          css={css`
            display: flex;
            align-items: center;
            gap: var(--fabric-space-gap-xs);
          `}
        >
          <RadioField.Radio css={styles.radio} value={SCHEDULING_MODES.WEAVE_REQUEST_SCHEDULING}>
            {t('Enable Appointment Requests')}
          </RadioField.Radio>
          <div
            {...triggerProps}
            css={css`
              cursor: pointer;
            `}
          >
            <Icon color='subdued' name='info-small' />
          </div>
          <Tooltip {...tooltipProps} style={{ ...tooltipProps.style, maxWidth: '280px' }}>
            {t(
              'When an appointment is requested, AI Receptionist will be responsible for capturing the request and alerting your team. A captured appointment will never be confirmed by AI Receptionist.'
            )}
          </Tooltip>
        </div>

        {showDirectBookingOption ? (
          <RadioField.Radio css={styles.radio} value={SCHEDULING_MODES.WEAVE_DIRECT_SCHEDULING}>
            {t('Enable direct booking')}
          </RadioField.Radio>
        ) : null}
      </RadioField>

      {mode === SCHEDULING_MODES.WEAVE_DIRECT_SCHEDULING && (
        <InlineNotification
          status='info'
          message={t(
            "AI Receptionist is set to initiate direct booking. AI Receptionist will no longer submit Appointment Requests. Remember to click 'Save' to apply these changes."
          )}
        />
      )}
    </div>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-lg);
    padding: var(--fabric-space-gap-lg);
    border: var(--fabric-border-width-thin) solid var(--fabric-color-accent-neutral-muted);
    border-radius: var(--fabric-radius-md);
  `,
  header: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-xs);
  `,
  radioFieldContainer: css`
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-lg);
  `,
  radio: css`
    margin: 0;
  `,
};
